import React, { FC, useState, useCallback } from "react";

import {
    useUpdateOnboardingAvailabilityMutation,
    useGetAppInfoQuery,
    CrmServiceAvailabilitySettingsInput,
} from "generated/graphql";
import { wrapUrqlMutation } from "utils/graphql";
import { ClientApp } from "models/App";

import { AvailabilityStep, AvailabilityStepProps } from "../../components/setup/AvailabilityStep";

export interface AvailabilityStepContainerProps extends Omit<AvailabilityStepProps, "onNext" | "app"> {
    app?: Pick<ClientApp, "availability" | "appId" | "organizationId">;
    onNext(): void;
    onBack?(): void;
}

export const AvailabilityStepContainer: FC<AvailabilityStepContainerProps> = (props) => {
    const { app, onNext, onBack, ...restProps } = props;

    const [saving, setSaving] = useState(false);

    // Get the app data to pre-populate the form
    const [getAppInfo] = useGetAppInfoQuery({
        pause: !app?.appId,
        requestPolicy: "cache-and-network",
        variables: {
            appId: app?.appId || "",
        },
    });

    const { fetching: loading, stale: isStale } = getAppInfo;

    // Setup the deep-merging availability mutation
    const updateAvailability = wrapUrqlMutation(useUpdateOnboardingAvailabilityMutation(), {
        onSuccessMessage: false,
    });

    const handleNext = useCallback(
        async (appData: Pick<ClientApp, "availability" | "appId" | "organizationId">) => {
            setSaving(true);

            try {
                // `updateAvailability` deep-merges server-side, so this step sends only the two
                // fields it actually owns. That is what keeps it from wiping availabilityClasses,
                // defaultAvailabilityClass, maxTotalDailyAppointments, delayedJobTypes and
                // blockedDays - the whole-object-replace `updateApp` used to delete every one of
                // them on each onboarding run. Skipping the call entirely when there is nothing to
                // save also means we never write `availability: {}`.
                if (appData?.appId && appData.availability) {
                    const availability: CrmServiceAvailabilitySettingsInput = {
                        availableDays: appData.availability.availableDays,
                        defaultBusyDays: appData.availability.defaultBusyDays,
                    };

                    await updateAvailability({
                        appId: appData.appId,
                        availability,
                    });
                }

                // Success - proceed to next step
                onNext();
            } catch (err) {
                console.error("Failed to update availability:", err);
                // Still proceed to next step even if save fails - we don't want to block the user
                onNext();
            } finally {
                setSaving(false);
            }
        },
        [onNext, updateAvailability]
    );

    return (
        <AvailabilityStep
            {...restProps}
            app={app}
            onNext={handleNext}
            onBack={onBack}
            saving={saving}
            loading={loading || isStale}
        />
    );
};
